import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ButtonSize, ButtonVariant, UpboTag } from '@/types'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, UpboTag {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: ReactNode
  iconRight?: ReactNode
  loading?: boolean
  fullWidth?: boolean
}

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-ink text-on-ink hover:bg-primary hover:text-on-primary',
  secondary: 'bg-surface-2 text-text hover:bg-border',
  outline: 'border border-border-strong bg-transparent text-text hover:border-text hover:bg-surface-2',
  ghost: 'text-text-muted hover:text-text hover:bg-surface-2',
  danger: 'bg-error text-white hover:opacity-90',
  accent: 'bg-primary text-on-primary hover:bg-primary-hover',
}

const sizes: Record<ButtonSize, string> = {
  sm: 'h-9 px-4 text-[11px] gap-1.5',
  md: 'h-11 px-6 text-[12px] gap-2',
  lg: 'h-[52px] px-8 text-[12px] gap-2.5',
  icon: 'h-10 w-10 p-0 text-[12px]',
}

/**
 * The one button of the house: square corners, letterspaced caps, no gloss.
 * Every clickable action on the site goes through this component.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon,
    iconRight,
    loading = false,
    fullWidth = false,
    disabled,
    children,
    className,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // `type="button"` by default: a button inside a form that forgets to
      // declare its type submits the form by accident — the classic bug.
      // Submit buttons must say `type="submit"` explicitly.
      type={type}
      data-upbo-type="button"
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center rounded-squircle font-medium uppercase tracking-[0.14em] whitespace-nowrap',
        'transition-colors duration-300',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-text focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        'disabled:opacity-45 disabled:pointer-events-none',
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 size={14} className="animate-spin shrink-0" /> : icon}
      {children}
      {!loading && iconRight}
    </button>
  )
})
